import * as THREE from 'three/webgpu';
import {
    isAbstractSketchEdgeObject,
    isSketchCollectionObject,
    isSketchSlotObject,
} from '../../../mesh/sketch';
import {
    createExistingHistorySnapshot,
    createMissingHistorySnapshot,
} from '../../../history';
import { createSketchEntityReferenceHistoryPatch } from '../../../history/domain/SketchEntityReferenceHistoryAdapter';
import type { SketchRelationDefinition } from '../../../sketch/solver/constraint/relationDefinition/SketchRelationDefinition';
import type { ISketchObject } from '../../../mesh/sketch';
import type {
    SketchEntityReferenceMoveDetachContext,
    SketchEntityReferenceMoveDetachInput,
    SketchEntityReferenceMoveDetachPort,
    SketchEntityReferenceMoveRepositoryPort,
} from '../../../../types/reference/sketch-entity-reference/SketchEntityReferenceMoveDetachTypes';
import {
    clearSketchEntityReferenceTag,
    readSketchEntityReferenceTag,
    writeSketchEntityReferenceTag,
} from '../model/SketchEntityReferenceIds';
import { createSketchEntityReferenceHistorySnapshot } from '../history/SketchEntityReferenceHistorySnapshot';
import type { SketchEntityReferenceRecord } from '../model/SketchEntityReferenceRecord';

/**
 * 移动转换实体前解除外部引用的领域服务。
 *
 * 设计说明：
 * 1. 只删除引用 ID 匹配的引用驱动约束，不误删水平、相切、尺寸等普通设计关系；
 * 2. 预览阶段同时清理实体引用标记，并通过上下文支持取消回滚；
 * 3. 宿主提供同步仓储端口时，同步删除引用记录并生成历史补丁，保证 Undo/Redo 原子恢复；
 * 4. 对直线、圆、圆弧、椭圆和样条等统一按引用标记处理，不写几何类型分支。
 */
export class SketchEntityReferenceMoveDetachService implements SketchEntityReferenceMoveDetachPort {
    /**
     * @param repository 可选的同步引用仓储端口。
     * @param detachRuntimeRelation 可选的引用关系运行时解绑入口。
     * @param resolveCurrentTargetEntity 可选的当前草图目标实体解析入口。
     */
    public constructor(
        private readonly repository: SketchEntityReferenceMoveRepositoryPort | null = null,
        private readonly detachRuntimeRelation: ((
            reference: SketchEntityReferenceRecord,
            targetEntity: THREE.Object3D,
        ) => void) | null = null,
        private readonly resolveCurrentTargetEntity: ((
            reference: SketchEntityReferenceRecord,
        ) => THREE.Object3D | null) | null = null,
    ) {}

    /**
     * 解除当前移动选择中的转换实体引用。
     *
     * @param input 解除输入。
     * @returns 可提交或回滚的解除上下文。
     */
    public detach(input: SketchEntityReferenceMoveDetachInput): SketchEntityReferenceMoveDetachContext {
        const referencedTargets = this.collectReferencedTargets(input.selectedEntities);
        const removedRecords = referencedTargets.map((targetEntity) => {
            const tag = readSketchEntityReferenceTag(targetEntity);
            if (!tag) {
                throw new Error(`转换实体 ${targetEntity.uuid} 缺少可用引用标记。`);
            }

            const reference = this.repository?.loadReferenceSync(tag.referenceId) ?? null;
            const constraints = this.collectReferenceConstraints(targetEntity, tag.referenceId);
            if (reference) {
                // 优先通知引用关系分发器清理内部 binding，避免 Undo 后重建产生重复约束。
                this.detachRuntimeRelation?.(reference, targetEntity);
            }
            constraints.forEach((constraint) => {
                targetEntity.constraints.removeRelation(constraint.id);
            });
            clearSketchEntityReferenceTag(targetEntity);
            this.repository?.deleteReferenceSync(tag.referenceId);

            return {
                targetEntity,
                tag,
                reference,
                constraints,
            };
        });
        const historyPatches = removedRecords.flatMap((record) => {
            if (!record.reference) {
                return [];
            }

            return [createSketchEntityReferenceHistoryPatch({
                entityId: record.reference.referenceId,
                operation: 'delete',
                before: createExistingHistorySnapshot(
                    createSketchEntityReferenceHistorySnapshot(record.reference),
                ),
                after: createMissingHistorySnapshot(),
                metadata: {
                    sketchId: record.reference.targetSketchId,
                },
            })];
        });
        let committed = false;

        return {
            removedReferenceCount: removedRecords.length,
            removedConstraintCount: removedRecords.reduce(
                (count, record) => count + record.constraints.length,
                0,
            ),
            historyPatches,
            commit(): void {
                committed = true;
            },
            rollback: (): void => {
                if (committed) {
                    return;
                }

                removedRecords.forEach((record) => {
                    if (record.reference) {
                        this.repository?.restoreReferenceSync(record.reference);
                    }
                    const currentTargetEntity = record.reference
                        ? this.resolveCurrentTargetEntity?.(record.reference) ?? record.targetEntity
                        : record.targetEntity;
                    writeSketchEntityReferenceTag(currentTargetEntity, record.tag);

                    // 历史失败恢复可能已经用 before 快照替换草图对象；此时约束由快照负责恢复，
                    // 这里只在仍是原对象时补回原约束实例，避免把绑定旧目标的约束挂到新对象上。
                    if (currentTargetEntity === record.targetEntity) {
                        record.constraints.forEach((constraint) => {
                            record.targetEntity.constraints.addRelation(
                                constraint as Parameters<typeof record.targetEntity.constraints.addRelation>[0],
                            );
                        });
                    }
                });
            },
        };
    }

    /**
     * 收集选择域中带转换实体引用标记的草图边。
     *
     * @param selectedEntities 当前移动选择。
     * @returns 去重后的转换实体目标集合。
     */
    private collectReferencedTargets(
        selectedEntities: readonly THREE.Object3D[],
    ): Array<THREE.Object3D & ISketchObject> {
        const targets = new Map<string, THREE.Object3D & ISketchObject>();
        const visited = new Set<string>();
        const visit = (entity: THREE.Object3D): void => {
            if (visited.has(entity.uuid)) {
                return;
            }
            visited.add(entity.uuid);

            if (isAbstractSketchEdgeObject(entity) && readSketchEntityReferenceTag(entity)) {
                targets.set(entity.uuid, entity);
            }
            if (isSketchCollectionObject(entity)) {
                entity.collectSet.forEach(visit);
            }
            if (isSketchSlotObject(entity)) {
                entity.getEdges().forEach(visit);
            }
            entity.children.forEach(visit);
        };

        selectedEntities.forEach(visit);
        return [...targets.values()];
    }

    /**
     * 收集目标实体上属于指定转换引用的驱动约束。
     *
     * @param targetEntity 转换实体目标。
     * @param referenceId 引用记录 ID。
     * @returns 引用 ID 匹配的约束集合。
     */
    private collectReferenceConstraints(
        targetEntity: ISketchObject,
        referenceId: string,
    ): Array<SketchRelationDefinition<ISketchObject>> {
        return targetEntity.constraints.getRelations()
            .filter((constraint) => Reflect.get(constraint, 'referenceId') === referenceId)
            .map((constraint) => constraint as SketchRelationDefinition<ISketchObject>);
    }
}
